import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import api, { type Quote, type QuoteRequest } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import PremiumHeader from '../components/PremiumHeader'
import LifecycleBoard from '../components/LifecycleBoard'

function fmtMoney(cents?: number | null) {
  if (typeof cents !== 'number') return '—'
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '—'
}

function toDateTimeLocalValue(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function statusLabel(status: QuoteRequest['status']) {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function QuotesPage() {
  const { hasRole } = useAuth()
  const isDriver = hasRole('DRIVER')
  const canDelete = hasRole('SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'CARRIER_ADMIN', 'DISPATCHER', 'BROKER_ADMIN')
  const qc = useQueryClient()
  const [message, setMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [savingRequestId, setSavingRequestId] = useState<string | null>(null)
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)
  const [quotedPriceInput, setQuotedPriceInput] = useState('')
  const [quoteMessageInput, setQuoteMessageInput] = useState('')
  const [quoteInternalNotesInput, setQuoteInternalNotesInput] = useState('')
  const [quoteExpiryInput, setQuoteExpiryInput] = useState('')
  const [paymentRequiredBeforePickup, setPaymentRequiredBeforePickup] = useState(false)
  const [paymentRequirementReason, setPaymentRequirementReason] = useState('')
  const [rejectReasonInput, setRejectReasonInput] = useState('')
  const [lastResponseUrl, setLastResponseUrl] = useState<string | null>(null)

  const demoSlug = 'wahatoya-auto-group'
  const publicQuoteFormPath = `/q/${demoSlug}`
  const publicQuoteFormUrl = `https://diamond-dispatch.pages.dev${publicQuoteFormPath}`

  const q = useQuery({
    queryKey: ['quotes'],
    queryFn: () => api.quotes.list().then(r => r.data),
  })

  const quoteRequestsQuery = useQuery({
    queryKey: ['quote-requests'],
    queryFn: () => api.quoteRequests.list().then((r) => r.data),
  })

  const deleteMut = useMutation({
    mutationFn: (quoteId: string) => api.quotes.delete(quoteId),
    onSuccess: () => {
      setErrorMessage(null)
      setMessage('Quote deleted.')
      qc.invalidateQueries({ queryKey: ['quotes'] })
    },
    onError: (err: any) => {
      setMessage(null)
      setErrorMessage(err?.response?.data?.error ?? 'Failed to delete quote.')
    },
  })

  const updateQuoteRequestStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: QuoteRequest['status'] }) => api.quoteRequests.updateStatus(id, status),
    onMutate: async ({ id, status }) => {
      setSavingRequestId(id)
      setErrorMessage(null)
      setMessage(null)
      await qc.cancelQueries({ queryKey: ['quote-requests'] })
      const previous = qc.getQueryData<QuoteRequest[]>(['quote-requests']) ?? []
      qc.setQueryData<QuoteRequest[]>(['quote-requests'], (current) =>
        (current ?? []).map((request) => request.id === id ? { ...request, status } : request)
      )
      return { previous }
    },
    onSuccess: () => {
      setMessage('Quote request updated.')
    },
    onError: (err: any, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['quote-requests'], ctx.previous)
      setMessage(null)
      setErrorMessage(err?.response?.data?.error ?? 'Failed to update quote request.')
    },
    onSettled: () => {
      setSavingRequestId(null)
      qc.invalidateQueries({ queryKey: ['quote-requests'] })
    },
  })

  const sendQuoteMutation = useMutation({
    mutationFn: (payload: {
      id: string
      quotedCustomerPriceCents: number
      quoteMessage?: string
      quoteInternalNotes?: string
      quoteExpiresAt?: string | null
      paymentRequiredBeforePickup?: boolean
      paymentRequirementReason?: string
    }) => api.quoteRequests.sendQuote(payload.id, {
      quotedCustomerPriceCents: payload.quotedCustomerPriceCents,
      quoteMessage: payload.quoteMessage,
      quoteInternalNotes: payload.quoteInternalNotes,
      quoteExpiresAt: payload.quoteExpiresAt,
      paymentRequiredBeforePickup: payload.paymentRequiredBeforePickup,
      paymentRequirementReason: payload.paymentRequirementReason,
    }),
    onSuccess: (response) => {
      setErrorMessage(null)
      const delivery = response.data.emailDelivery
      if (delivery.deliveryStatus === 'sent') {
        setMessage('Quote sent to customer.')
      } else if (delivery.deliveryStatus === 'provider_not_configured') {
        setMessage('Quote queued. Email provider not configured.')
      } else {
        setMessage('Quote queued for delivery.')
      }
      setLastResponseUrl(response.data.quoteResponseUrl)
      qc.invalidateQueries({ queryKey: ['quote-requests'] })
    },
    onError: (err: any) => {
      setMessage(null)
      setErrorMessage(err?.response?.data?.error ?? 'Failed to send quote.')
    },
  })

  const rejectQuoteRequestMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.quoteRequests.reject(id, reason),
    onSuccess: (response) => {
      setErrorMessage(null)
      const delivery = response.data.emailDelivery
      if (delivery.deliveryStatus === 'sent') {
        setMessage('Rejection sent to customer.')
      } else if (delivery.deliveryStatus === 'provider_not_configured') {
        setMessage('Rejection queued. Email provider not configured.')
      } else {
        setMessage('Rejection queued for delivery.')
      }
      qc.invalidateQueries({ queryKey: ['quote-requests'] })
    },
    onError: (err: any) => {
      setMessage(null)
      setErrorMessage(err?.response?.data?.error ?? 'Failed to reject quote request.')
    },
  })

  const quotes: Quote[] = q.data ?? []
  const quoteRequests: QuoteRequest[] = (quoteRequestsQuery.data ?? []).filter((request: any) => {
    const metadata = request?.metadata && typeof request.metadata === 'object' ? request.metadata : {}
    if (String((metadata as any).demoVisibility ?? '').toLowerCase() === 'hidden') return false
    if (String(request.status || '').toLowerCase() === 'archived') return false
    return true
  })
  const selectedRequest = quoteRequests.find((request) => request.id === selectedRequestId) || null

  const lifecycleStages = [
    'Quote Request',
    'Working Quote',
    'Quote Sent',
    'Quote Accepted',
    'Customer Completing Details',
    'Customer Submitted',
    'Dispatch Review',
    'Ready for Dispatch',
  ]

  function lifecycleSummary(request: QuoteRequest) {
    const missing: string[] = []
    const blocked: string[] = []

    if (!request.deliveryZip) missing.push('Missing delivery ZIP.')
    if (!request.deliveryCity || !request.deliveryState) missing.push('Missing delivery city/state.')
    if (!request.deliveryContactName && !request.metadata?.deliveryContactName) missing.push('Missing delivery contact.')
    if (request.status === 'waiting_on_customer') missing.push('Customer has not provided all completion details.')

    if (request.status === 'expired') blocked.push('Quote expired and needs re-quote.')
    if (request.status === 'duplicate') blocked.push('Marked duplicate; requires manual review.')
    if (request.status === 'archived') blocked.push('Archived request is not active for dispatch.')
    if (request.status === 'declined') blocked.push('Customer declined this quote.')

    const ownerByStatus: Record<string, string> = {
      new: 'Dispatcher',
      assigned: 'Assigned Dispatcher',
      in_progress: 'Quote Desk',
      waiting_on_customer: 'Customer',
      quote_sent: 'Customer',
      accepted: 'Customer',
      declined: 'Dispatcher',
      expired: 'Dispatcher',
      duplicate: 'Dispatcher',
      archived: 'Dispatcher',
      converted: 'Dispatch',
      rejected: 'Dispatcher',
    }

    const nextActionByStatus: Record<string, { label: string; to?: string }> = {
      new: { label: 'Start working quote' },
      assigned: { label: 'Open pricing workspace' },
      in_progress: { label: 'Send quote to customer' },
      waiting_on_customer: { label: 'Send completion link' },
      quote_sent: { label: 'Follow up with customer' },
      accepted: { label: 'Open customer completion portal' },
      declined: { label: 'Archive or re-engage customer' },
      expired: { label: 'Re-quote request' },
      duplicate: { label: 'Resolve duplicate and merge record' },
      archived: { label: 'Keep archived' },
      converted: { label: 'Create trip', to: '/loads/new' },
      rejected: { label: 'Close request' },
    }

    const timeline = [
      { label: lifecycleStages[0], at: request.createdAt },
      ...(request.status !== 'new' ? [{ label: lifecycleStages[1], at: request.updatedAt }] : []),
      ...(request.quotedAt ? [{ label: lifecycleStages[2], at: request.quotedAt }] : []),
      ...(request.customerAcceptedAt ? [{ label: lifecycleStages[3], at: request.customerAcceptedAt }] : []),
      ...(request.customerAcceptedAt ? [{ label: lifecycleStages[4], at: request.customerAcceptedAt }] : []),
      ...(request.customerAcceptedAt ? [{ label: lifecycleStages[5], at: request.updatedAt }] : []),
      ...(request.status === 'converted' ? [{ label: lifecycleStages[6], at: request.updatedAt }, { label: lifecycleStages[7], at: request.updatedAt }] : []),
    ]

    const next = nextActionByStatus[request.status] ?? { label: 'Review request' }

    return {
      currentStatus: statusLabel(request.status),
      owner: ownerByStatus[request.status] ?? 'Dispatcher',
      missing,
      blocked,
      next,
      timeline,
    }
  }

  function handleSelectRequest(request: QuoteRequest) {
    setSelectedRequestId(request.id)
    setQuotedPriceInput(request.quotedCustomerPriceCents ? String((request.quotedCustomerPriceCents / 100).toFixed(2)) : '')
    setQuoteMessageInput(request.quoteMessage || '')
    setQuoteInternalNotesInput(request.quoteInternalNotes || '')
    setQuoteExpiryInput(toDateTimeLocalValue(request.quoteExpiresAt))
    setPaymentRequiredBeforePickup(Boolean(request.paymentRequiredBeforePickup))
    setPaymentRequirementReason(request.paymentRequirementReason || '')
    setRejectReasonInput(request.rejectionReason || '')
    setLastResponseUrl(null)
  }

  function submitQuote() {
    if (!selectedRequest) return
    const parsed = Number(quotedPriceInput)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setMessage(null)
      setErrorMessage('Enter a valid quote price.')
      return
    }
    const quotedCustomerPriceCents = Math.round(parsed * 100)
    sendQuoteMutation.mutate({
      id: selectedRequest.id,
      quotedCustomerPriceCents,
      quoteMessage: quoteMessageInput.trim() || undefined,
      quoteInternalNotes: quoteInternalNotesInput.trim() || undefined,
      quoteExpiresAt: quoteExpiryInput ? new Date(quoteExpiryInput).toISOString() : null,
      paymentRequiredBeforePickup,
      paymentRequirementReason: paymentRequirementReason.trim() || undefined,
    })
  }

  function submitRejection() {
    if (!selectedRequest) return
    const reason = rejectReasonInput.trim()
    if (!reason) {
      setMessage(null)
      setErrorMessage('Rejection reason is required.')
      return
    }
    rejectQuoteRequestMutation.mutate({ id: selectedRequest.id, reason })
  }

  async function handleCopyQuoteFormLink() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicQuoteFormUrl)
      } else {
        const input = document.createElement('input')
        input.value = publicQuoteFormUrl
        document.body.appendChild(input)
        input.select()
        const copied = document.execCommand('copy')
        document.body.removeChild(input)
        if (!copied) throw new Error('copy failed')
      }
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2000)
    } catch {
      setCopyState('failed')
      setTimeout(() => setCopyState('idle'), 2500)
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-5">
      <PremiumHeader
        title="Quote Desk"
        subtitle="Price moves and work QuotePort requests from one workspace."
        primaryAction={
          <Link to="/quotes/new" className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800">
            New Quote
          </Link>
        }
      />

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <p className="text-sm text-gray-600">Send this public QuotePort link to a customer so they can request a transport quote.</p>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={publicQuoteFormPath}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800 hover:bg-blue-100 font-semibold"
          >
            Open Public Quote Form
          </a>
          <button
            type="button"
            onClick={handleCopyQuoteFormLink}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Copy Quote Form Link
          </button>
          {copyState === 'copied' && <span className="text-xs text-green-700">Copied!</span>}
          {copyState === 'failed' && <span className="text-xs text-red-600">Copy failed. Use the link button.</span>}
        </div>
        <p className="text-xs text-gray-500">Submitted quote requests appear here.</p>
      </div>

      {q.isLoading && <div className="text-sm text-gray-500">Loading quotes...</div>}
      {q.isError && <div className="text-sm text-red-600">Failed to load quotes.</div>}
      {quoteRequestsQuery.isError && <div className="text-sm text-red-600">Failed to load QuotePort requests.</div>}
      {message && <div className="mb-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">{message}</div>}
      {errorMessage && <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{errorMessage}</div>}

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="border-b bg-gray-50 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">QuotePort Requests</h2>
          <p className="text-xs text-gray-500 mt-1">Converted quotes can be turned into dispatch jobs in the next workflow.</p>
        </div>

        {!quoteRequestsQuery.isLoading && !quoteRequests.length && (
          <div className="p-6 text-sm text-gray-500 space-y-2">
            <div>No quote requests yet.</div>
            <div>Send this public QuotePort link to a customer so they can request a transport quote.</div>
          </div>
        )}

        {!!quoteRequests.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Vehicle</th>
                  <th className="text-left px-3 py-2">Pickup</th>
                  <th className="text-left px-3 py-2">Delivery</th>
                  <th className="text-left px-3 py-2">VIN Status</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {quoteRequests.map((request) => (
                  <tr
                    key={request.id}
                    className={`border-b last:border-b-0 hover:bg-gray-50 cursor-pointer ${selectedRequestId === request.id ? 'bg-blue-50' : ''}`}
                    onClick={() => handleSelectRequest(request)}
                  >
                    <td className="px-3 py-2">{fmtDate(request.createdAt)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{request.customerName}</div>
                      <div className="text-xs text-gray-500">{request.customerEmail} · {request.customerPhone}</div>
                    </td>
                    <td className="px-3 py-2">{request.vehicleYear ?? ''} {request.vehicleMake ?? ''} {request.vehicleModel ?? ''}</td>
                    <td className="px-3 py-2">{request.pickupCity}, {request.pickupState} {request.pickupZip}</td>
                    <td className="px-3 py-2">{request.deliveryCity}, {request.deliveryState} {request.deliveryZip}</td>
                    <td className="px-3 py-2 capitalize">{request.vinStatus.replace('_', ' ')}</td>
                    <td className="px-3 py-2">{statusLabel(request.status)}</td>
                    <td className="px-3 py-2">
                      <select
                        className="rounded border px-2 py-1 text-xs"
                        value={request.status}
                        onChange={(e) => {
                          e.stopPropagation()
                          updateQuoteRequestStatus.mutate({ id: request.id, status: e.target.value as QuoteRequest['status'] })
                        }}
                        disabled={savingRequestId === request.id}
                      >
                        <option value="new">New</option>
                        <option value="assigned">Assigned</option>
                        <option value="in_progress">In Progress</option>
                        <option value="waiting_on_customer">Waiting on Customer</option>
                        <option value="quote_sent">Quote Sent</option>
                        <option value="accepted">Accepted</option>
                        <option value="declined">Declined</option>
                        <option value="expired">Expired</option>
                        <option value="duplicate">Duplicate</option>
                        <option value="archived">Archived</option>
                        <option value="converted">Converted</option>
                      </select>
                      {savingRequestId === request.id && <span className="ml-2 text-xs text-gray-500">Saving…</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedRequest && (
        <div className="bg-white border rounded-xl p-4 space-y-4">
          <LifecycleBoard
            title="Quote to Dispatch Lifecycle"
            currentStatus={lifecycleSummary(selectedRequest).currentStatus}
            owner={lifecycleSummary(selectedRequest).owner}
            missing={lifecycleSummary(selectedRequest).missing}
            blocked={lifecycleSummary(selectedRequest).blocked}
            nextBestAction={lifecycleSummary(selectedRequest).next.label}
            nextActionTo={lifecycleSummary(selectedRequest).next.to}
            timeline={lifecycleSummary(selectedRequest).timeline}
          />

          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Quote Request Detail</h3>
              <p className="text-xs text-gray-500">
                {selectedRequest.customerName} • {selectedRequest.pickupCity}, {selectedRequest.pickupState} → {selectedRequest.deliveryCity}, {selectedRequest.deliveryState}
              </p>
            </div>
            <span className="text-xs rounded-full border px-2 py-1">{statusLabel(selectedRequest.status)}</span>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3 rounded-lg border p-3">
              <h4 className="text-sm font-semibold text-gray-900">Send Quote</h4>
              <label className="block text-xs text-gray-600">
                Customer Price (USD)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={quotedPriceInput}
                  onChange={(e) => setQuotedPriceInput(e.target.value)}
                  className="mt-1 w-full rounded border px-2 py-1 text-sm"
                  placeholder="1450.00"
                />
              </label>
              <label className="block text-xs text-gray-600">
                Quote Message
                <textarea
                  value={quoteMessageInput}
                  onChange={(e) => setQuoteMessageInput(e.target.value)}
                  className="mt-1 h-20 w-full rounded border px-2 py-1 text-sm"
                  placeholder="We can pick up in 24 hours and deliver in 2-3 days."
                />
              </label>
              <label className="block text-xs text-gray-600">
                Internal Notes
                <textarea
                  value={quoteInternalNotesInput}
                  onChange={(e) => setQuoteInternalNotesInput(e.target.value)}
                  className="mt-1 h-16 w-full rounded border px-2 py-1 text-sm"
                  placeholder="Visible internally only."
                />
              </label>
              <label className="block text-xs text-gray-600">
                Quote Expires At
                <input
                  type="datetime-local"
                  value={quoteExpiryInput}
                  onChange={(e) => setQuoteExpiryInput(e.target.value)}
                  className="mt-1 w-full rounded border px-2 py-1 text-sm"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={paymentRequiredBeforePickup}
                  onChange={(e) => setPaymentRequiredBeforePickup(e.target.checked)}
                />
                Payment required before pickup (informational only)
              </label>
              {paymentRequiredBeforePickup && (
                <label className="block text-xs text-gray-600">
                  Payment Requirement Note
                  <input
                    type="text"
                    value={paymentRequirementReason}
                    onChange={(e) => setPaymentRequirementReason(e.target.value)}
                    className="mt-1 w-full rounded border px-2 py-1 text-sm"
                    placeholder="Payment due before pickup release."
                  />
                </label>
              )}

              <button
                type="button"
                onClick={submitQuote}
                disabled={sendQuoteMutation.isPending}
                className="rounded bg-blue-700 text-white px-3 py-2 text-sm font-semibold hover:bg-blue-800 disabled:opacity-50"
              >
                {sendQuoteMutation.isPending ? 'Sending...' : 'Send Quote to Customer'}
              </button>

              {lastResponseUrl && (
                <div className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800 break-all">
                  Customer response link: <a href={lastResponseUrl} target="_blank" rel="noopener noreferrer" className="underline">{lastResponseUrl}</a>
                </div>
              )}
            </div>

            <div className="space-y-3 rounded-lg border p-3">
              <h4 className="text-sm font-semibold text-gray-900">Reject Request</h4>
              <label className="block text-xs text-gray-600">
                Rejection Reason
                <textarea
                  value={rejectReasonInput}
                  onChange={(e) => setRejectReasonInput(e.target.value)}
                  className="mt-1 h-28 w-full rounded border px-2 py-1 text-sm"
                  placeholder="No carrier capacity on requested pickup window."
                />
              </label>
              <button
                type="button"
                onClick={submitRejection}
                disabled={rejectQuoteRequestMutation.isPending}
                className="rounded border border-red-300 text-red-700 px-3 py-2 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
              >
                {rejectQuoteRequestMutation.isPending ? 'Sending...' : 'Reject and Notify Customer'}
              </button>

              <div className="text-xs text-gray-500 space-y-1">
                <p>Quoted at: {fmtDate(selectedRequest.quotedAt)}</p>
                <p>Accepted at: {fmtDate(selectedRequest.customerAcceptedAt)}</p>
                <p>Declined at: {fmtDate(selectedRequest.customerDeclinedAt)}</p>
                <p>Rejected at: {fmtDate(selectedRequest.rejectedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!q.isLoading && !quotes.length && (
        <div className="text-sm text-gray-500 bg-white border rounded-xl p-6">No internal quotes yet. Create your first quote.</div>
      )}

      {!!quotes.length && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="border-b bg-gray-50 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Internal Quotes</h2>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2">Quote</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Route</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">Customer Quote</th>
                <th className="text-left px-3 py-2">Carrier Target</th>
                <th className="text-left px-3 py-2">Margin</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((quote) => (
                <tr key={quote.id} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <Link to={`/quotes/${quote.id}`} className="text-blue-700 font-medium hover:underline">{quote.quoteNumber}</Link>
                  </td>
                  <td className="px-3 py-2">{quote.status}</td>
                  <td className="px-3 py-2">{quote.customerName ?? '—'}</td>
                  <td className="px-3 py-2">{quote.originZip ?? '—'} → {quote.destinationZip ?? '—'}</td>
                  <td className="px-3 py-2">{quote.vehicleYear ?? ''} {quote.vehicleMake ?? ''} {quote.vehicleModel ?? ''}</td>
                  <td className="px-3 py-2">{fmtMoney(quote.customerQuoteCents)}</td>
                  <td className="px-3 py-2">{fmtMoney(quote.targetCarrierPayCents)}</td>
                  <td className="px-3 py-2">{fmtMoney(quote.grossMarginCents)}</td>
                  <td className="px-3 py-2">{quote.updatedAt ? new Date(quote.updatedAt).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2">
                    {canDelete && !isDriver && (
                      <button
                        type="button"
                        onClick={() => {
                          setMessage(null)
                          setErrorMessage(null)
                          if (!confirm('Are you sure you want to delete this quote?')) return
                          deleteMut.mutate(quote.id)
                        }}
                        disabled={deleteMut.isPending}
                        className="text-xs rounded border border-red-300 px-2 py-1 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  )
}
